<template>
  <v-container fluid class="te-page">
    <page-skeleton v-if="loading" variant="cards" class="mt-4" />

    <template v-else>
      <!-- Retorno de 30/10: volta às aulas do módulo. -->
      <router-link to="/connect/aluno/curso" class="te-page__back">
        <v-icon size="18" class="te-page__back-icon">mdi-arrow-left</v-icon>
        {{ $t("teacherExtras.back") }}
      </router-link>

      <header class="te-page__head">
        <p class="te-page__eyebrow">{{ $t("teacherExtras.eyebrow") }}</p>
        <h1 class="te-page__title">{{ $t("teacherExtras.title") }}</h1>
        <p class="te-page__sub">{{ $t("teacherExtras.subtitle") }}</p>
      </header>

      <p v-if="failed" class="te-page__empty">
        {{ $t("teacherExtras.loadError") }}
      </p>

      <p v-else-if="!items.length" class="te-page__empty">
        {{ $t("teacherExtras.empty") }}
      </p>

      <template v-else>
        <section v-for="group in groups" :key="group.key" class="te-group">
          <h2 class="te-group__label">
            {{ $t(`teacherExtras.${group.key}`) }}
            <span class="te-group__count">{{ group.items.length }}</span>
          </h2>

          <ul class="te-list">
            <li
              v-for="item in group.items"
              :key="item.id"
              class="te-row"
              :class="{ 'te-row--viewed': item.viewed }"
            >
              <div class="te-row__main">
                <span class="te-row__title">{{ item.title }}</span>
                <span class="te-row__meta">
                  {{ item.moduleTitle }}
                  <template v-if="item.teacher">
                    · {{ $t("teacherExtras.from", { name: item.teacher }) }}
                  </template>
                  · {{ formatDate(item.sharedAt) }}
                </span>
                <span v-if="item.note" class="te-row__note">
                  “{{ item.note }}”
                </span>
              </div>

              <div class="te-row__actions">
                <v-btn text small class="te-row__btn" @click="open(item)">
                  <v-icon left size="18">fi-rr-book-alt</v-icon>
                  {{ $t("teacherExtras.open") }}
                </v-btn>
                <v-btn
                  text
                  small
                  class="te-row__btn"
                  :loading="busyId === item.id"
                  @click="toggleViewed(item)"
                >
                  <v-icon left size="18">
                    {{ item.viewed ? "fi-rr-undo" : "fi-rr-check" }}
                  </v-icon>
                  {{
                    item.viewed
                      ? $t("teacherExtras.unmark")
                      : $t("teacherExtras.mark")
                  }}
                </v-btn>
              </div>
            </li>
          </ul>
        </section>
      </template>
    </template>
  </v-container>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";

// Retorno de 29/10 (card 17trt6jfxtn): "Indicados pelo professor". Tudo o que
// o professor recomendou, de qualquer nível, com marca de visto. Extra visto
// entra nos assuntos concluídos do certificado.
export default {
  name: "TeacherExtrasPage",

  data() {
    return {
      loading: true,
      failed: false,
      busyId: "",
      items: [],
    };
  },

  computed: {
    // Pendentes primeiro; dentro de cada grupo, o mais recente no topo.
    groups() {
      const byDate = (a, b) =>
        new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime();
      const pending = this.items.filter((item) => !item.viewed).sort(byDate);
      const viewed = this.items.filter((item) => item.viewed).sort(byDate);

      return [
        { key: "pending", items: pending },
        { key: "viewed", items: viewed },
      ].filter((group) => group.items.length);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      return pitchCustomerApi
        .getTeacherExtras()
        .then((res) => {
          this.items = res.data?.data || [];
        })
        .catch(() => {
          this.failed = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    formatDate(value) {
      return new Date(value).toLocaleDateString(this.$i18n.locale, {
        timeZone: "America/Sao_Paulo",
      });
    },

    // Por path: o nome "Aulas" também pode existir na área do professor.
    open(item) {
      this.$router.push(`/connect/aluno/curso/${item.contentId}`);
    },

    toggleViewed(item) {
      this.busyId = item.id;
      const done = !item.viewed;
      return pitchCustomerApi
        .setTeacherExtraViewed(item.id, done)
        .then(() => {
          item.viewed = done;
          item.viewedAt = done ? new Date().toISOString() : null;
        })
        .catch(() => {
          this.$root.$emit("display-alert", {
            text: this.$t("teacherExtras.markError"),
            type: 1,
          });
        })
        .finally(() => {
          this.busyId = "";
        });
    },
  },
};
</script>

<style scoped>
.te-page {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: var(--dk-line, #ece6ef);
  --surface: var(--dk-surface, #fff);
  --pink: var(--dk-pink, #e8276f);
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 12px 56px;
  font-family: "Poppins", sans-serif;
  color: var(--ink);
}

.te-page__back {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 18px;
  padding: 6px 2px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--muted);
  text-decoration: none;
}
.te-page__back:hover {
  color: var(--ink);
}
.te-page__back-icon {
  color: inherit !important;
}

.te-page__head {
  margin-bottom: 28px;
}

.te-page__eyebrow {
  margin: 0 0 8px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--pink);
}

.te-page__title {
  margin: 0;
  font-size: clamp(1.6rem, 4vw, 2.1rem);
  font-weight: 700;
  line-height: 1.2;
}

.te-page__sub {
  margin: 10px 0 0;
  max-width: 60ch;
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--muted);
}

.te-page__empty {
  color: var(--muted);
}

.te-group + .te-group {
  margin-top: 32px;
}

.te-group__label {
  margin: 0 0 12px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.te-group__count {
  margin-left: 6px;
  font-weight: 400;
}

.te-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid var(--line);
}

.te-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}

.te-row--viewed .te-row__title {
  color: var(--muted);
}

.te-row__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.te-row__title {
  font-weight: 600;
}

.te-row__meta {
  font-size: 0.82rem;
  color: var(--muted);
}

.te-row__note {
  margin-top: 4px;
  font-size: 0.82rem;
  font-style: italic;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.te-row__actions {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}

.te-row__btn.v-btn {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: var(--pink);
}

@media (max-width: 600px) {
  .te-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .te-row__actions {
    justify-content: flex-start;
  }
}
</style>
